import { CSSProperties } from 'react';
import { Button, Popconfirm, Tooltip, Typography } from 'antd';
import { TrackedCard, KtStagingArea, Area } from '../../redux/api/kit-staging';
import { Area as ClientArea, lookup as lookupRule } from './moveRulesClient';

interface PropsWithExtras {
	/** Fires when a same-area target should be moved straight to a specific shelf,
	 *  bypassing the inline shelf picker (e.g. material joining a kit already on JSA). */
	onAutoPlaceShelf?: (card: TrackedCard, target: 'msa' | 'jsa', shelfId: number) => void;
}

interface Props extends PropsWithExtras {
	kitCard: TrackedCard | null;
	materialCard: TrackedCard | null;
	stagingAreas: KtStagingArea[];
	/** Active tab key — when set, sections for cards NOT in this area are
	 *  rendered disabled with a hint, so e.g. the Cart view doesn't show
	 *  actionable buttons for material that's still on MSA. */
	activeTab?: string;
	onInitiate: (card: TrackedCard, target_area: Area | 'machine', target_shelf?: number) => void;
	onConfirm: (card: TrackedCard, target_area?: Area | 'machine') => void;
	onUndo: (card: TrackedCard) => void;
	onOverride: (card: TrackedCard) => void;
	onDelete: (card: TrackedCard) => void;
	onPickShelf: (card: TrackedCard, target: 'msa' | 'jsa') => void;
	onMarkPickedUp?: () => void;
}

/** Tablet-optimised unified action block for an OliGroup.
 *  Compact status header + 2-row button grid sized for touch. */
export function GroupActionBlock({
	kitCard,
	materialCard,
	stagingAreas,
	activeTab,
	onInitiate,
	onConfirm,
	onUndo,
	onOverride,
	onDelete,
	onPickShelf,
	onMarkPickedUp,
}: Props) {
	const rep = kitCard ?? materialCard;
	if (!rep) return null;

	const job = rep.job;
	const kitReady = !!job?.kit_ready;
	const materialReady = !!job?.material_ready;
	const materialPartial = !!job?.material_partial;

	// Per-status gating (mirrored on the backend in _validate_status_gate):
	//  - Q4.1 Cancelled        → only DEKIT.
	//  - Q4.2 On hold          → only DEKIT or UNASSIGNED.
	//  - Q4.3 Site mismatch    → reroute (JSA) or DEKIT (or UNASSIGNED).
	//  - Q2.3 Task mismatch    → MACHINE, DEKIT, or UNASSIGNED.
	const anyOnHold = kitCard?.status === 'on_hold' || materialCard?.status === 'on_hold';
	const anyCancelled = kitCard?.status === 'cancelled' || materialCard?.status === 'cancelled';
	const anySiteMismatch = kitCard?.status === 'site_mismatch' || materialCard?.status === 'site_mismatch';
	const anyTaskMismatch =
		kitCard?.status === 'task_status_mismatch' || materialCard?.status === 'task_status_mismatch';
	const anyAbnormal = anyOnHold || anyCancelled || anySiteMismatch || anyTaskMismatch;
	const blockedReason = anyCancelled
		? 'Cancelled — only Dekit allowed.'
		: anySiteMismatch
			? 'Site mismatch — reroute (JSA) or Dekit only.'
			: anyTaskMismatch
				? 'Task mismatch — Machine or Dekit only.'
				: anyOnHold
					? 'On hold — release in ERP first.'
					: '';

	// JSA is shared-shelf — both kit and material can go there together. The
	// button is enabled if EITHER card can legally move to JSA and kit is ready
	// (Roy's gate for "kitting task done"). Both already on JSA = nothing to do.
	const bothOnJsa = kitCard?.current_area === 'jsa' && materialCard?.current_area === 'jsa';
	// Ready for machine setup: both kit and material on the same JSA shelf,
	// operator hasn't yet taken it. SINGLE_ERP path on the backend.
	const readyForSetup =
		bothOnJsa &&
		kitCard?.current_shelf != null &&
		kitCard.current_shelf === materialCard?.current_shelf &&
		!kitCard?.setup_pending;
	// Forward placements are blocked for abnormal statuses. The per-item
	// ItemActionSection re-evaluates this per card with its own canMove() and
	// readiness flags.
	// Mark Picked Up CTA is driven by the DekitItem state, not the
	// TrackedCard's current_area — so the button shows even if the
	// associated cards are archived/at-machine, as long as a pickup is
	// outstanding for the OLI. Falls back to "any card in dekit" for older
	// job summaries missing the has_unpicked_dekit field.
	const anyInDekit =
		job?.has_unpicked_dekit === true ||
		(job?.has_unpicked_dekit === undefined &&
			(kitCard?.current_area === 'dekit' || materialCard?.current_area === 'dekit'));
	// alreadyPickedUp: every DekitItem for this OLI is picked up (no unpicked
	// items remain) AND at least one has been picked up.
	const alreadyPickedUp =
		job?.has_unpicked_dekit === false && (kitCard?.picked_up || materialCard?.picked_up) === true;
	const handleMarkPickedUp = () => {
		if (onMarkPickedUp) onMarkPickedUp();
	};

	const pendingCard =
		(kitCard?.pending_target_area ? kitCard : null) ?? (materialCard?.pending_target_area ? materialCard : null);
	// A pair (both kit + material pending into the same area) is confirmed on
	// the destination board as ONE card — never split via a single-card
	// confirm here. The sidebar just shows the pending state + a pointer.
	const bothPending =
		kitCard?.pending_target_area != null &&
		materialCard?.pending_target_area != null &&
		kitCard.pending_target_area === materialCard.pending_target_area;

	if (pendingCard) {
		// Resolve pending_target_shelf -> human-readable "Area · Rack · Shelf".
		const pendingShelfId = pendingCard.pending_target_shelf;
		let pendingLocation: string | null = null;
		if (pendingShelfId != null) {
			outer: for (const a of stagingAreas ?? []) {
				for (const r of a.racks ?? []) {
					for (const s of r.shelves ?? []) {
						if (s.id === pendingShelfId) {
							pendingLocation = `${a.name} · Rack ${r.label} · Shelf ${s.position}`;
							break outer;
						}
					}
				}
			}
		}
		return (
			<div style={{ marginTop: 10 }}>
				<div
					style={{
						background: '#fffbe6',
						border: '1px solid #faad14',
						borderRadius: 4,
						padding: '6px 10px',
						marginBottom: 8,
					}}
				>
					<div style={{ fontSize: 12, fontWeight: 600 }}>
						{pendingCard.item_type === 'kit' ? 'Kit' : 'Material'} pending →{' '}
						<Typography.Text code>{String(pendingCard.pending_target_area).toUpperCase()}</Typography.Text>
					</div>
					{pendingLocation && <div style={{ fontSize: 12, color: '#874d00', marginTop: 2 }}>→ {pendingLocation}</div>}
				</div>
				{bothPending ? (
					<div
						style={{
							fontSize: 11,
							color: '#874d00',
							background: '#fffbe6',
							border: '1px dashed #ffd591',
							borderRadius: 4,
							padding: '8px 10px',
							lineHeight: 1.4,
						}}
					>
					Kit &amp; material are moving together. Confirm or undo this pair on its{' '}
						<b>{String(pendingCard.pending_target_area).toUpperCase()}</b> card.
					</div>
				) : (
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
						<Button type="primary" style={btnStyle} onClick={() => onConfirm(pendingCard)}>
							Confirm
						</Button>
						<Button danger style={btnStyle} onClick={() => onUndo(pendingCard)}>
							Undo
						</Button>
					</div>
				)}
			</div>
		);
	}

	const handleTakeForSetup = () => {
		if (!kitCard || !readyForSetup) return;
		// JSA → MACHINE is SINGLE_ERP: one operator confirm marks setup_pending;
		// ERP advancing setup status soft-hides the card via the auto-route.
		onConfirm(kitCard, 'machine');
	};

	// Resolve a card's shelf -> staging area name + id via the topology
	// tree. Returns null when the card isn't placed on a shelf yet.
	const resolveAreaForCard = (card: TrackedCard | null): { id: number; name: string } | null => {
		if (!card?.current_shelf) return null;
		for (const a of stagingAreas ?? []) {
			for (const r of a.racks ?? []) {
				for (const s of r.shelves ?? []) {
					if (s.id === card.current_shelf) return { id: a.id, name: a.name };
				}
			}
		}
		return null;
	};
	const kitArea = resolveAreaForCard(kitCard);
	const materialArea = resolveAreaForCard(materialCard);
	// Same-OLI split: both cards are on JSA but on different staging
	// areas. The OLI tile renders in two places, and Take-for-Setup is
	// blocked because the cards aren't on the same shelf. Surface this
	// plainly so the operator knows to reroute the partner.
	const splitAcrossJsas =
		kitCard?.current_area === 'jsa' &&
		materialCard?.current_area === 'jsa' &&
		!kitCard.is_soft_hidden &&
		!materialCard.is_soft_hidden &&
		kitArea &&
		materialArea &&
		kitArea.id !== materialArea.id;

	return (
		<div style={{ marginTop: 10 }}>
			{/* Status rows */}
			<div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
				<StatusRow
					label="Kit"
					card={kitCard}
					atMachine={kitCard?.is_soft_hidden === true || (!kitCard && job?.kit_at_machine === true)}
				/>
				<StatusRow
					label="Material"
					card={materialCard}
					atMachine={materialCard?.is_soft_hidden === true || (!materialCard && job?.material_at_machine === true)}
				/>
			</div>

			{splitAcrossJsas && (
				<div
					style={{
						marginBottom: 10,
						padding: '6px 10px',
						background: '#fff1f0',
						border: '1px solid #ff7875',
						borderRadius: 4,
						fontSize: 11,
						color: '#cf1322',
						lineHeight: 1.35,
					}}
				>
					<div style={{ fontWeight: 700, marginBottom: 2 }}>Kit &amp; Material in different JSAs</div>
					<div style={{ color: '#874d00' }}>
						KIT → <b>{kitArea!.name}</b>
						<br />
						MATERIAL → <b>{materialArea!.name}</b>
						<br />
						Reroute the mismatched card so both share a shelf before setup.
					</div>
				</div>
			)}

			{/* Highlight: Mark Picked Up CTA when any card is in dekit */}
			{anyInDekit && !alreadyPickedUp && (
				<div style={{ marginBottom: 10 }}>
					<Button
						type="primary"
						onClick={handleMarkPickedUp}
						style={{ width: '100%', height: 44, fontWeight: 700, fontSize: 14, background: '#13c2c2', borderColor: '#13c2c2' }}
					>
						Mark Picked Up
					</Button>
				</div>
			)}
			{alreadyPickedUp && (
				<div
					style={{
						marginBottom: 10,
						padding: '8px 10px',
						borderRadius: 6,
						background: '#e6fffb',
						border: '1px solid #87e8de',
						fontSize: 12,
						color: '#08979c',
					}}
				>
					<b>Picked up</b> — collected from the dekit area.
				</div>
			)}

			{/* Highlight: machine-setup CTA when both cards share a JSA shelf */}
			{readyForSetup && (
				<div style={{ marginBottom: 10 }}>
					<Button
						type="primary"
						onClick={handleTakeForSetup}
						style={{
							width: '100%',
							height: 44,
							fontWeight: 700,
							fontSize: 14,
							background: '#52c41a',
							borderColor: '#52c41a',
						}}
					>
						Take for Setup
					</Button>
				</div>
			)}
			{/* Per-item action sections — separate kit and material so the
          operator can act on each card independently. */}
			{kitCard && (
				<ItemActionSection
					card={kitCard}
					status={kitCard.status}
					kitReady={kitReady}
					materialReady={materialReady}
					materialPartial={materialPartial}
					blocked={anyAbnormal}
					blockedReason={blockedReason}
					anySiteMismatch={anySiteMismatch}
					anyTaskMismatch={anyTaskMismatch}
					contextDisabled={
						!!activeTab && activeTab !== 'overview' && activeTab !== 'returnable' && kitCard.current_area !== activeTab
					}
					contextLabel={activeTab ? activeTab.toUpperCase() : ''}
					onConfirm={onConfirm}
					onOverride={onOverride}
					onDelete={onDelete}
					onPickShelf={onPickShelf}
					onInitiate={onInitiate}
				/>
			)}
			{materialCard && (
				<ItemActionSection
					card={materialCard}
					status={materialCard.status}
					kitReady={kitReady}
					materialReady={materialReady}
					materialPartial={materialPartial}
					blocked={anyAbnormal}
					blockedReason={blockedReason}
					anySiteMismatch={anySiteMismatch}
					anyTaskMismatch={anyTaskMismatch}
					contextDisabled={
						!!activeTab &&
						activeTab !== 'overview' &&
						activeTab !== 'returnable' &&
						materialCard.current_area !== activeTab
					}
					contextLabel={activeTab ? activeTab.toUpperCase() : ''}
					onConfirm={onConfirm}
					onOverride={onOverride}
					onDelete={onDelete}
					onPickShelf={onPickShelf}
					onInitiate={onInitiate}
				/>
			)}
		</div>
	);
}

interface SectionProps {
	card: TrackedCard;
	status: string;
	kitReady: boolean;
	materialReady: boolean;
	materialPartial: boolean;
	blocked: boolean;
	blockedReason: string;
	anySiteMismatch: boolean;
	anyTaskMismatch: boolean;
	/** When true, disable the section because the card isn't in the active
	 *  area's context (e.g. material isn't on Cart but the operator is on the
	 *  Cart tab). Still visible — informational only. */
	contextDisabled?: boolean;
	contextLabel?: string;
	onConfirm: (c: TrackedCard, ta?: Area | 'machine') => void;
	onInitiate: (c: TrackedCard, ta: Area | 'machine', ts?: number) => void;
	onOverride: (c: TrackedCard) => void;
	onDelete: (c: TrackedCard) => void;
	onPickShelf: (c: TrackedCard, target: 'msa' | 'jsa') => void;
}

function ItemActionSection({
	card,
	kitReady,
	materialReady,
	materialPartial,
	blocked,
	blockedReason,
	anySiteMismatch,
	anyTaskMismatch,
	contextDisabled,
	contextLabel,
	onConfirm,
	onInitiate,
	onOverride,
	onDelete,
	onPickShelf,
}: SectionProps) {
	const isKit = card.item_type === 'kit';
	const label = isKit ? 'KIT' : 'MATERIAL';
	const labelColor = isKit ? '#1890ff' : '#faad14';
	const from = card.current_area as ClientArea;

	const canMove = (target: 'cart' | 'msa' | 'jsa' | 'dekit' | 'machine') => lookupRule(from, target) !== 'NOT_ALLOWED';

	const ownReady = (isKit && kitReady) || (!isKit && (materialReady || materialPartial));
	const ctxBlocked = !!contextDisabled;
	const cartAllowed = !ctxBlocked && !blocked && ownReady && canMove('cart');
	// MSA only applies to material; for kit, hide the button.
	const showMsa = !isKit;
	const msaAllowed = !ctxBlocked && !blocked && showMsa && (materialReady || materialPartial) && canMove('msa');
	// JSA gate uses kit_ready regardless of item_type (backend rule).
	const jsaAllowed = !ctxBlocked && (anySiteMismatch || !blocked) && kitReady && canMove('jsa');
	// Roy Q2.3: task_status_mismatch options are "let it stand / move to dekit /
	// move to machine" — so Machine stays available even when blocked by status.
	const machineAllowed = !ctxBlocked && (anyTaskMismatch || !blocked) && kitReady && canMove('machine');
	const dekitAllowed = !ctxBlocked && canMove('dekit'); // dekit always allowed under abnormal — but not when out-of-context

	const handleCart = () => {
		if (cartAllowed) onConfirm(card, 'cart');
	};
	const handleMsa = () => {
		if (showMsa && msaAllowed) onPickShelf(card, 'msa');
	};
	const handleJsa = () => {
		if (jsaAllowed) onPickShelf(card, 'jsa');
	};
	const handleMachine = () => {
		if (!machineAllowed) return;
		const rule = lookupRule(from, 'machine');
		if (rule === 'SINGLE' || rule === 'SINGLE_ERP') onConfirm(card, 'machine');
		else onInitiate(card, 'machine');
	};
	const handleDekit = () => {
		if (dekitAllowed) onConfirm(card, 'dekit');
	};

	const deleteAllowed = card.current_area === 'unassigned';

	return (
		<div
			style={{
				marginTop: 12,
				padding: 8,
				borderRadius: 6,
				background: '#fafafa',
				border: '1px solid #f0f0f0',
				opacity: ctxBlocked ? 0.55 : 1,
			}}
		>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 6,
					fontSize: 11,
					fontWeight: 700,
					color: '#262626',
					marginBottom: 6,
				}}
			>
				<span
					style={{
						background: labelColor,
						color: '#fff',
						padding: '2px 6px',
						borderRadius: 4,
						letterSpacing: 0.5,
					}}
				>
					{label}
				</span>
				<span style={{ color: '#8c8c8c', fontWeight: 400, fontSize: 11 }}>{card.current_area.toUpperCase()}</span>
				{ctxBlocked && (
					<span
						style={{
							marginLeft: 'auto',
							fontSize: 9,
							fontWeight: 700,
							letterSpacing: 0.5,
							color: '#8c8c8c',
							background: '#f0f0f0',
							padding: '2px 6px',
							borderRadius: 4,
						}}
					>
						NOT IN {contextLabel}
					</span>
				)}
			</div>

			<div style={{ display: 'grid', gridTemplateColumns: showMsa ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr', gap: 6 }}>
				{cartAllowed ? (
					<Button style={btnStyle} onClick={handleCart}>
						Cart
					</Button>
				) : (
					<Tooltip
						title={ctxBlocked ? `${label} is not in ${contextLabel}` : blocked ? blockedReason : 'Cart not legal from here.'}
					>
						<Button style={btnStyle} disabled>
							Cart
						</Button>
					</Tooltip>
				)}
				{showMsa &&
					(msaAllowed ? (
						<Button style={btnStyle} onClick={handleMsa}>
							MSA
						</Button>
					) : (
						<Tooltip
							title={ctxBlocked ? `${label} is not in ${contextLabel}` : blocked ? blockedReason : 'Material not ready'}
						>
							<Button style={btnStyle} disabled>
								MSA
							</Button>
						</Tooltip>
					))}
				{jsaAllowed ? (
					<Button type="primary" style={btnStyle} onClick={handleJsa}>
						{anySiteMismatch ? 'JSA (reroute)' : 'JSA'}
					</Button>
				) : (
					<Tooltip title={ctxBlocked ? `${label} is not in ${contextLabel}` : blocked ? blockedReason : 'Kit not ready'}>
						<Button style={btnStyle} disabled>
							JSA
						</Button>
					</Tooltip>
				)}
				{machineAllowed ? (
					<Button type={anyTaskMismatch ? 'primary' : 'default'} style={btnStyle} onClick={handleMachine}>
						{anyTaskMismatch ? 'Machine (catch up)' : 'Machine'}
					</Button>
				) : (
					<Tooltip
						title={
							ctxBlocked
								? `${label} is not in ${contextLabel}`
								: blocked
									? blockedReason
									: !kitReady
										? 'Kit not ready'
										: 'Machine not legal.'
						}
					>
						<Button style={btnStyle} disabled>
							Machine
						</Button>
					</Tooltip>
				)}
			</div>

			<div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: deleteAllowed ? '1fr 1fr 1fr' : '1fr 1fr', gap: 6 }}>
				{dekitAllowed ? (
					<Button style={btnStyle} onClick={handleDekit}>
						Dekit
					</Button>
				) : (
					<Tooltip title="Cannot Dekit from this area.">
						<Button style={btnStyle} disabled>
							Dekit
						</Button>
					</Tooltip>
				)}
				<Button style={btnStyle} onClick={() => onOverride(card)}>
					Override…
				</Button>
				{deleteAllowed && (
					<Popconfirm
						title={`Delete ${label.toLowerCase()} card?`}
						description="Sync will not recreate it until an admin unblocks the tombstone."
						onConfirm={() => onDelete(card)}
					>
						<Button danger style={btnStyle}>
							Delete
						</Button>
					</Popconfirm>
				)}
			</div>
		</div>
	);
}

const btnStyle: CSSProperties = {
	height: 38,
	fontSize: 13,
	fontWeight: 600,
	padding: 0,
};

const AREA_COLORS: Record<string, { bg: string; fg: string; border: string }> = {
	unassigned: { bg: '#f5f5f5', fg: '#595959', border: '#d9d9d9' },
	cart: { bg: '#e6f4ff', fg: '#0958d9', border: '#91caff' },
	msa: { bg: '#fff7e6', fg: '#d46b08', border: '#ffd591' },
	jsa: { bg: '#f6ffed', fg: '#389e0d', border: '#b7eb8f' },
	dekit: { bg: '#fff1f0', fg: '#cf1322', border: '#ffa39e' },
};

function StatusRow({ label, card, atMachine }: { label: string; card: TrackedCard | null; atMachine?: boolean }) {
	const area = card?.current_area as string | undefined;
	const c = area ? AREA_COLORS[area] : null;
	// Machine chip — shown when card is filtered (soft-hidden at machine).
	const machineStyle: CSSProperties = {
		fontSize: 11,
		fontWeight: 700,
		textTransform: 'uppercase',
		padding: '2px 8px',
		borderRadius: 10,
		background: '#fff7e6',
		color: '#d46b08',
		border: '1px solid #ffd591',
		letterSpacing: 0.4,
	};
	return (
		<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
			<span style={{ fontSize: 12, fontWeight: 600, color: '#262626' }}>{label}</span>
			{atMachine ? (
				<span style={machineStyle}>machine</span>
			) : card && c ? (
				<span
					style={{
						fontSize: 11,
						fontWeight: 700,
						textTransform: 'uppercase',
						padding: '2px 8px',
						borderRadius: 10,
						background: c.bg,
						color: c.fg,
						border: `1px solid ${c.border}`,
						letterSpacing: 0.4,
					}}
				>
					{area}
				</span>
			) : (
				<span style={{ fontSize: 11, color: '#bfbfbf' }}>—</span>
			)}
		</div>
	);
}
